package top.wcpe.allincore.ui.runtime.element

import top.wcpe.allincore.ui.runtime.event.ChangeEvent
import top.wcpe.allincore.ui.runtime.event.SliderInputTarget
import top.wcpe.allincore.ui.runtime.event.UiEventHandler
import top.wcpe.allincore.ui.runtime.internal.log.UiLog
import top.wcpe.allincore.ui.runtime.internal.math.decimalStepAlign
import top.wcpe.allincore.ui.runtime.reactive.Effect
import top.wcpe.allincore.ui.runtime.reactive.Signal
import kotlin.jvm.JvmStatic
import kotlin.math.roundToLong

/**
 * 滑块控件。
 *
 * 数据绑定：双向连接响应式 [Signal]，用户拖动时反向写 signal；
 * [setValueFromRatio] 为交互入口（由 [InputDispatcher] / [ControlInteractions] 调用），
 * 接收归一化比例并做 step 量化与 [min,max] 钳制后写入 value。
 *
 * 视觉覆盖（Task 2a）：所有可空字段为显式覆盖值；`null` 时回退主题 token，由 [SliderRenderer] 解析。
 *
 * @see top.wcpe.allincore.ui.runtime.render.SliderRenderer
 * @see top.wcpe.allincore.ui.runtime.event.InputDispatcher
 */
class Slider private constructor() :
    UIElement<Slider>(),
    SliderInputTarget {

    // ===== 核心数据（backing 字段用 Value/Handler/Signal 后缀避 ktlint "backing property" 规则）=====
    private var currentValue: Double = DEFAULT_VALUE
    private var minValue: Double = DEFAULT_MIN
    private var maxValue: Double = DEFAULT_MAX
    private var stepValue: Double = DEFAULT_STEP
    private var onChangeHandler: UiEventHandler<ChangeEvent<Double>>? = null
    private var boundValueSignal: Signal<Any?>? = null

    // ===== 视觉覆盖字段（可空；null = 回退主题 token，由 SliderRenderer 解析）=====
    /** 轨道色（ARGB）；null 时回退主题 surfaceContainerHighest。 */
    private var trackColorValue: Int? = null

    /** 已填充段色（ARGB）；null 时回退主题 primary。 */
    private var fillColorValue: Int? = null

    /** 手柄色（ARGB）；null 时回退主题 primary。 */
    private var thumbColorValue: Int? = null

    /** 轨道高度（像素）；null 时回退主题 sliderTrackHeight。 */
    private var trackHeightValue: Float? = null

    /** 手柄边长（像素）；null 时回退主题 sliderThumbSize。 */
    private var thumbSizeValue: Float? = null

    /** 轨道圆角半径（像素）；null 或 0 时轨道为直角（走 fillRect）。 */
    private var trackCornerRadiusValue: Float? = null

    /** 箭头三角色（ARGB）；null 时回退主题 primary。 */
    private var arrowColorValue: Int? = null

    /** 状态层圆颜色（ARGB）；null 时回退主题 primary。 */
    private var stateLayerColorValue: Int? = null

    /** 状态层圆尺寸（像素）；null 时回退主题 stateLayerSize。 */
    private var stateLayerSizeValue: Float? = null

    // 状态层逐态覆盖：null 时回退主题默认
    private var hoverOverlayColorValue: Int? = null
    private var hoverOverlayOpacityValue: Float? = null
    private var pressedOverlayColorValue: Int? = null
    private var pressedOverlayOpacityValue: Float? = null
    private var focusOverlayColorValue: Int? = null
    private var focusOverlayOpacityValue: Float? = null

    init {
        this.focusableField = true
    }

    // ===== CRTP 自引用 =====
    override fun self(): Slider = this

    // ===== 核心 fluent setter（保留方法式签名供 Java 调用方）=====

    /**
     * 写入当前值。若值变更：同步反向写 bound signal 并触发 onChange；
     * DSL 回调异常被隔离（记 WARN 并吞掉），控件状态不受影响。
     */
    fun value(v: Double): Slider {
        val old = currentValue
        currentValue = v
        val changed = old.compareTo(v) != 0
        if (changed) {
            boundValueSignal?.set(v)
            // 隔离：DSL onChange 回调异常不得冒泡到输入派发线程。
            // 已完成 value 写入与 bound signal 同步，回调异常仅记 WARN 并吞掉，控件状态保持一致。
            try {
                onChangeHandler?.accept(ChangeEvent(old, v))
            } catch (t: Throwable) {
                logger.warn("[ui] Slider(id=${id()}) onChange 回调异常已隔离；旧值=$old 新值=$v", t)
            }
        }
        return this
    }

    /** 写入下限，后续 setValueFromRatio 自动钳制。 */
    fun min(v: Double): Slider {
        minValue = v
        return this
    }

    /** 写入上限，后续 setValueFromRatio 自动钳制。 */
    fun max(v: Double): Slider {
        maxValue = v
        return this
    }

    /** 写入步进粒度；step>0 时鼠标写入会量化到 step 的整数倍。 */
    fun step(v: Double): Slider {
        stepValue = v
        return this
    }

    /**
     * 由交互层（[InputDispatcher]）调用：根据鼠标在轨道内的相对比例（0..1）写入 value，
     * 自动做 step 量化与 [min,max] 钳制。
     *
     * 本方法是 Slider 与输入派发器之间的唯一交互桥；Slider 不感知 LayoutResult。
     *
     * @param ratio 鼠标 X 在轨道内的归一化位置（可以越界，会被钳制）
     */
    fun setValueFromRatio(ratio: Double): Slider {
        if (minValue.compareTo(maxValue) == 0) {
            return value(minValue)
        }
        var v = minValue + ratio * (maxValue - minValue)
        // 量化到 step 网格
        if (stepValue > 0.0) {
            val steps = ((v - minValue) / stepValue).roundToLong()
            v = decimalStepAlign(stepValue, steps, minValue)
        }
        // 钳制到 [min, max]
        v = v.coerceIn(minValue, maxValue)
        return value(v)
    }

    /**
     * 动画系统写入当前显示值，不触发 onChange，也不反向写 bound signal。
     *
     * 服务端 state 或用户输入仍应走 [value] / [setValueFromRatio]。
     */
    fun setAnimatedValue(v: Double): Slider {
        currentValue = v
        return this
    }

    /** 注册值变更回调（每次 value 写入且值变化时触发）。 */
    fun onChange(handler: UiEventHandler<ChangeEvent<Double>>): Slider {
        onChangeHandler = handler
        return this
    }

    /**
     * 双向绑定到响应式 [Signal]。
     * 绑定值非数字时 [toDoubleValue] 抛 `IllegalStateException`：刻意保留 fail-fast——
     * 真正的类型错误应向上抛出示警，由上游安全网兜底；事件 Map 等可恢复误用已在 [toDoubleValue] 内兜底。
     */
    @Suppress("UNCHECKED_CAST")
    fun bindValue(signal: Signal<*>): Slider {
        val objectSignal = signal as Signal<Any?>
        boundValueSignal = objectSignal
        val e = Effect { currentValue = toDoubleValue(objectSignal.get()) }
        lifecycleScopeField.register(e)
        return this
    }

    // ===== getter（保留方法式签名供 Java / Kotlin 调用方）=====

    override fun value(): Double = currentValue
    override fun min(): Double = minValue
    override fun max(): Double = maxValue
    override fun step(): Double = stepValue

    // ===== FR-UI-025：SliderInputTarget 命令方法（另起名避开 fluent setter 的 Slider 返回类型）=====

    override fun applySliderValue(v: Double) {
        value(v)
    }

    override fun applySliderRatio(ratio: Double) {
        setValueFromRatio(ratio)
    }

    // ===== Task 2a：视觉覆盖 fluent setter / getter ====
    // 方法引用形如 `Slider::trackColor` 仍由 Java ControlVisualPropContributor 消费；
    // Java 编译器按 getter（0 参）vs setter（1 参）区分重载，兼容无虞。

    fun trackColor(v: Int?): Slider {
        trackColorValue = v
        return this
    }
    fun trackColor(): Int? = trackColorValue
    fun fillColor(v: Int?): Slider {
        fillColorValue = v
        return this
    }
    fun fillColor(): Int? = fillColorValue
    fun thumbColor(v: Int?): Slider {
        thumbColorValue = v
        return this
    }
    fun thumbColor(): Int? = thumbColorValue
    fun trackHeight(v: Float?): Slider {
        trackHeightValue = v
        return this
    }
    fun trackHeight(): Float? = trackHeightValue
    fun thumbSize(v: Float?): Slider {
        thumbSizeValue = v
        return this
    }
    fun thumbSize(): Float? = thumbSizeValue
    fun trackCornerRadius(v: Float?): Slider {
        trackCornerRadiusValue = v
        return this
    }
    fun trackCornerRadius(): Float? = trackCornerRadiusValue
    fun arrowColor(v: Int?): Slider {
        arrowColorValue = v
        return this
    }
    fun arrowColor(): Int? = arrowColorValue
    fun stateLayerColor(v: Int?): Slider {
        stateLayerColorValue = v
        return this
    }
    fun stateLayerColor(): Int? = stateLayerColorValue
    fun stateLayerSize(v: Float?): Slider {
        stateLayerSizeValue = v
        return this
    }
    fun stateLayerSize(): Float? = stateLayerSizeValue
    fun hoverOverlayColor(v: Int?): Slider {
        hoverOverlayColorValue = v
        return this
    }
    fun hoverOverlayColor(): Int? = hoverOverlayColorValue
    fun hoverOverlayOpacity(v: Float?): Slider {
        hoverOverlayOpacityValue = v
        return this
    }
    fun hoverOverlayOpacity(): Float? = hoverOverlayOpacityValue
    fun pressedOverlayColor(v: Int?): Slider {
        pressedOverlayColorValue = v
        return this
    }
    fun pressedOverlayColor(): Int? = pressedOverlayColorValue
    fun pressedOverlayOpacity(v: Float?): Slider {
        pressedOverlayOpacityValue = v
        return this
    }
    fun pressedOverlayOpacity(): Float? = pressedOverlayOpacityValue
    fun focusOverlayColor(v: Int?): Slider {
        focusOverlayColorValue = v
        return this
    }
    fun focusOverlayColor(): Int? = focusOverlayColorValue
    fun focusOverlayOpacity(v: Float?): Slider {
        focusOverlayOpacityValue = v
        return this
    }
    fun focusOverlayOpacity(): Float? = focusOverlayOpacityValue

    companion object {
        private val logger = UiLog("top.wcpe.allincore.ui.runtime.element.Slider")

        /** 默认值（与控件创建时初始状态一致）。 */
        private const val DEFAULT_VALUE = 0.0

        /** 默认下限。 */
        private const val DEFAULT_MIN = 0.0

        /** 默认上限。 */
        private const val DEFAULT_MAX = 1.0

        /** 默认步进（0 = 连续、无量化）。 */
        private const val DEFAULT_STEP = 0.0

        /** 创建新的滑块实例。 */
        @JvmStatic
        fun create(): Slider = Slider()

        /**
         * 将绑定 signal 的值安全转换为 `double`。
         *
         * 加固：若误把 onChange 的事件 Map（形如 `{value=Number, oldValue=...}`）
         * 写回了 value state，取其 `value` 字段兜底，避免直接崩溃。
         * 常见于 DSL 作者写 `onChange = { v -> state.x = v }` 漏掉 `.value` 的场景。
         */
        private fun toDoubleValue(v: Any?): Double {
            if (v is Number) return v.toDouble()
            // 兜底：事件 Map 取 value 字段
            if (v is Map<*, *>) {
                val inner = v["value"]
                if (inner is Number) return inner.toDouble()
            }
            throw IllegalStateException("Slider 绑定的 value state 必须是数字: $v")
        }
    }
}
